import { Organization } from "../../../../models/Organization";
import { CustomThirdPartyProvider, Project } from "../../../../models/Project";
import { Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import { CheckCircleOutlined, Settings } from "@mui/icons-material";
import React, { useContext } from "react";
import { MoreMenu } from "../../../../components/User/MoreMenu";
import { CustomProviderModal } from "./CustomProviderModal";
import { ApiClient } from "../../../../client/ApiClient";
import { ConfirmationModal } from "../../../../components/ConfirmationModal";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { useRevalidator } from "react-router-dom";

interface Props {
  organization: Organization
  project: Project
}

export const CustomConnections = (props: Props) => {
  const { organization, project } = props
  const [open, setOpen] = React.useState(false)
  const [providerID, setProviderID] = React.useState<string | undefined>(undefined)
  const revalidator = useRevalidator()
  const { success, error } = useContext(SnackbarContext)

  const deleteProvider = async (id: String) => {
    const customProviders: Record<string, CustomThirdPartyProvider> = {}
    for (let key in project.third_party.custom_providers) {
      if (key !== id) {
        customProviders[key] = project.third_party.custom_providers[key]
      }
    }
    try {
      await ApiClient.updateProject(organization.id, project.id, {
        third_party: {
          ...project.third_party,
          custom_providers: customProviders,
        }
      })
      revalidator.revalidate()
      success("Provider successful deleted")
    } catch (e) {
      console.log(e)
      error("Failed to delete provider")
    }
  }

  const enableDisableProvider = async (id: String, enabled: boolean) => {
    const customProviders = project.third_party.custom_providers
    if (customProviders) {
      for (let key in project.third_party.custom_providers) {
        if (key === id) {
          customProviders[key].enabled = enabled
        }
      }
    }
    try {
      await ApiClient.updateProject(organization.id, project.id, {
        third_party: {
          ...project.third_party,
          custom_providers: customProviders,
        }
      })
      revalidator.revalidate()
      success("Provider successful enabled/disabled")
    } catch (e) {
      console.log(e)
      error("Failed to enable/disable provider")
    }
  }

  const startEditProvider = (id: string) => {
    setProviderID(id)
    setOpen(true)
  }

  let providerRows: React.ReactNode[] = []
  if (project.third_party.custom_providers) {
    for (const [id, provider] of Object.entries(project.third_party.custom_providers) as [string, CustomThirdPartyProvider][]) {
      providerRows.push(<ProviderTableRow key={id} provider={provider} id={id} deleteProvider={deleteProvider}
                                          enableDisableProvider={enableDisableProvider} setProviderId={startEditProvider} />)
    }
  }

  if (providerRows.length === 0) {
    providerRows.push(<TableRow>
      <TableCell colSpan={100} size="medium"><Typography variant="body2" sx={{
        color: '#7C8E9C'
      }}>No custom connections found.</Typography></TableCell>
    </TableRow>)
  }

  return <>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell colSpan={4}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 2, md: 4 },
              justifyContent: 'space-between'
            }}>
              <SettingsHead title="Custom connections" description={null} sx={{ mb: 0, gap: 0 }} />
              <Button variant="contained" onClick={() => setOpen(true)}>New connection</Button>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {providerRows}
      </TableBody>
    </Table>
    <CustomProviderModal organizationId={organization.id} project={project} id={providerID} open={open} onClose={() => {
      setOpen(false)
      setProviderID(undefined)
    }} />
  </>
}

interface TableRowProps {
  provider: CustomThirdPartyProvider
  id: string
  deleteProvider: (id: string) => Promise<void>
  enableDisableProvider: (id: string, enabled: boolean) => Promise<void>
  setProviderId: (id: string) => void
}

const ProviderTableRow = (props: TableRowProps) => {
  const { provider, id, setProviderId } = props
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  return <>
    <TableRow key={id}>
      <TableCell sx={{ width: 0 }}>
        <Settings style={{ width: '17px', height: '17px' }} />
      </TableCell>
      <TableCell>{provider.display_name}</TableCell>
      <TableCell sx={{ width: 0 }}>
        {props.provider.enabled ?
          <Chip label="Enabled" color="success" variant="outlined" icon={<CheckCircleOutlined color="success" />}
                sx={{ fontWeight: 500 }} /> :
          <Chip label="Disabled" color="primary" variant="outlined" sx={{ width: '100%', fontWeight: 500 }} />}
      </TableCell>
      <TableCell sx={{ width: 0 }}>
        <MoreMenu items={[{
          label: 'Edit',
          onClick: () => {
            setProviderId(id)
          }
        }, {
          label: provider.enabled ? 'Disable' : 'Enable',
          onClick: () => {
            props.enableDisableProvider(id, !provider.enabled)
          }
        }, {
          label: 'Delete',
          dangerous: true,
          onClick: () => setDeleteModalOpen(true)
        }]} />
      </TableCell>
    </TableRow>
    <ConfirmationModal title="Delete connection" buttonText="Delete connection"
                       description="Are you sure you want to delete this connection?" open={deleteModalOpen}
                       onClose={() => {
                         setDeleteModalOpen(false)
                       }}
                       onConfirm={async () => {
                         await props.deleteProvider(id)
                       }}
    />
  </>
}